// Appointment type codes, recovered from the long phrases an EHR export puts in
// its "Event"/"Type" column ("Follow Up - Sleep", "New Patient - SLEEP CONSULT").
//
// The four clinical codes are not opaque — they decompose into two independent
// halves, which is why detecting each half separately reconstructs all of them:
//
//     visit kind: New Patient -> "N"   Follow-up -> "FF"
//     specialty:  Pulmonary   -> "P"   Sleep     -> "S"
//
// giving NP / NS / FFP / FFS. GlobalSystemPromptManager reads exactly that
// split (NP|FFP -> Pulmonary Forms, NS|FFS -> Sleep Forms).
//
// Deliberately conservative, per the user's ruling: a phrase carrying only one
// half, or an AMBIGUOUS half, is returned untouched rather than guessed at. No
// clinical inference either — "asthma" is not read as pulmonary. Getting this
// wrong writes a wrong type onto a real chart, so silence beats a guess.
export const CANONICAL_TYPES = ['NP', 'NS', 'FFP', 'FFS'];

const CANONICAL = new Set(CANONICAL_TYPES);

// Punctuation becomes spaces, so "F/U", "Follow-Up" and "Follow Up" all reduce
// to the same token stream and one pattern set covers every separator style.
function normalize(text) {
  return String(text).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

const VISIT_KINDS = [
  { code: 'FF', patterns: [/\bfollow\s+up\b/, /\bfollowup\b/, /\bf\s+u\b/, /\bfu\b/] },
  { code: 'N', patterns: [/\bnew\s+patient\b/, /\bnew\s+pt\b/, /\bnew\b/] },
];

const SPECIALTIES = [
  { code: 'S', patterns: [/\bsleep\b/] },
  { code: 'P', patterns: [/\bpulmonary\b/, /\bpulm\b/] },
];

// The matching half, or null when nothing matched OR when more than one did —
// "Follow Up - Sleep and Pulmonary" names two specialties and picking either
// would be a coin flip.
function soleMatch(norm, groups) {
  const hits = groups.filter((g) => g.patterns.some((re) => re.test(norm)));
  return hits.length === 1 ? hits[0].code : null;
}

export function normalizeAppointmentType(value) {
  if (value == null || value === '') return value;

  const raw = String(value).trim();
  // Already one of ours (any casing) — normalise the casing and stop.
  if (CANONICAL.has(raw.toUpperCase())) return raw.toUpperCase();

  const norm = normalize(raw);
  if (!norm) return value;

  const visit = soleMatch(norm, VISIT_KINDS);
  const specialty = soleMatch(norm, SPECIALTIES);
  if (!visit || !specialty) return value;

  const code = `${visit}${specialty}`;
  // Belt and braces: only ever emit a code the app actually recognises.
  return CANONICAL.has(code) ? code : value;
}
